import React from 'react';
import { notification } from 'antd';
import QuoteStore from '../../../../stores/QuoteStore';
import { useValidationErrorsHandlers } from './useValidationErrorsHandlers';
import { AxiosError } from 'axios';
import { QuoteLineItemProps } from '../../../../types/types';

interface Params {
    setLoading: (v: boolean) => void;
    setErrorPartId: (v: number | null) => void;
    setErrorStepId: (v: number | null) => void;
    setScrollTick: React.Dispatch<React.SetStateAction<number>>;
    notifyError: (error: AxiosError) => void;
    zipCode: string;
}

export const useQuoteLineItemHandlers = ({
    setLoading,
    setErrorPartId,
    setErrorStepId,
    setScrollTick,
    notifyError,
    zipCode,
}: Params) => {
    const { handleQuoteValidationErrors } = useValidationErrorsHandlers({
        setLoading,
        setErrorPartId,
        setErrorStepId,
        setScrollTick,
    });

    const refreshQuoteMetadata = async (updateLineItems = true, useSummaryLoading = true) => {
        if (QuoteStore.getQuoteUid) {
            await QuoteStore.getQuoteByUid(QuoteStore.getQuoteUid, {
                updateLineItems: updateLineItems,
                useSummaryLoading: useSummaryLoading,
            });
        }
    };

    const updateLineItem = async (payload: any) => {
        // setLoading(true);
        await QuoteStore.updateQuoteLineItem(payload)
            .then(async () => {
                await refreshQuoteMetadata(true, true);
            })
            .catch(error => {
                notifyError(error);
            })
            .finally(() => {
                // setLoading(false);
                handleQuoteValidationErrors();
            });
    };

    const duplicateItem = async (masterProduct: number) => {
        setLoading(true);

        await QuoteStore.duplicateItem(masterProduct)
            .then(async () => {
                await refreshQuoteMetadata(true, true);
                notification.success({ message: 'Item duplicated successfully.' });
            })
            .catch(error => {
                notifyError(error);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const removeMasterProduct = async (id: number) => {
        await QuoteStore.deleteMasterProduct(id)
            .then(async () => {
                await refreshQuoteMetadata(true, true);
            })
            .catch(error => {
                setLoading(false);

                notifyError(error);
            });
    };

    const removeVariation = async (id: string) => {
        await QuoteStore.removeVariation(id)
            .then(async () => {
                await refreshQuoteMetadata(true, true);
            })
            .catch(error => {
                setLoading(false);

                notifyError(error);
            });
    };

    const updateItemPrice = async (itemId: number, leadTimeOptionId: string) => {
        setLoading(true);
        const payload = {
            id: itemId,
            price_id: leadTimeOptionId,
        };

        if (QuoteStore.getQuoteUid) {
            await QuoteStore.updateItemPrice(QuoteStore.getQuoteUid, payload)
                .then(async () => {
                    await refreshQuoteMetadata(true, true);
                    setLoading(false);
                })
                .catch(error => {
                    setLoading(false);

                    notifyError(error);
                });
        }
    };

    const onLineItemUpdated = async (lineItem: QuoteLineItemProps) => {
        // Find the existing line item in the store
        const existingLineItem = QuoteStore.line_items?.find(
            item => item.id === lineItem.id || item.uid === lineItem.uid
        );

        // Compare analysis status to detect any changes
        const incoming2dStatus = lineItem?.product?.analysis_status?.file_2d?.completed ?? false;
        const incoming3dStatus = lineItem?.product?.analysis_status?.file_3d?.completed ?? false;

        const existing2dStatus = existingLineItem?.product?.analysis_status?.file_2d?.completed ?? false;
        const existing3dStatus = existingLineItem?.product?.analysis_status?.file_3d?.completed ?? false;

        // Check if there's any change in analysis status (either file_2d or file_3d)
        const hasStatusChange = incoming2dStatus !== existing2dStatus || incoming3dStatus !== existing3dStatus;

        const quantityKey = lineItem?.quantity;
        const incomingPrices = lineItem?.pricing_options?.prices_by_quantity?.[quantityKey];
        const existingPrices = existingLineItem?.pricing_options?.prices_by_quantity?.[quantityKey];

        const incomingPriceSignature = JSON.stringify(
            (incomingPrices ?? []).map(option => ({
                id: option?.id,
                lead_time: option?.lead_time,
                unit_price: option?.unit_price,
                total_price: option?.total_price,
            }))
        );

        const existingPriceSignature = JSON.stringify(
            (existingPrices ?? []).map(option => ({
                id: option?.id,
                lead_time: option?.lead_time,
                unit_price: option?.unit_price,
                total_price: option?.total_price,
            }))
        );

        const hasPricingOptionsChanged =
            lineItem?.pricing_options?.part_id !== existingLineItem?.pricing_options?.part_id ||
            (incomingPrices?.length ?? 0) !== (existingPrices?.length ?? 0) ||
            incomingPriceSignature !== existingPriceSignature;

        const hasTopLevelPricingChanged =
            lineItem?.unit_price !== existingLineItem?.unit_price ||
            lineItem?.total_price !== existingLineItem?.total_price ||
            lineItem?.lead_time !== existingLineItem?.lead_time ||
            lineItem?.quote_action !== existingLineItem?.quote_action ||
            lineItem?.safe_auto_quote !== existingLineItem?.safe_auto_quote ||
            lineItem?.needs_manual_quote !== existingLineItem?.needs_manual_quote;

        console.log('hasPricingOptionsChanged', hasPricingOptionsChanged);

        if (hasPricingOptionsChanged || hasTopLevelPricingChanged || hasStatusChange) {
            await QuoteStore.setQuoteLineItem(lineItem)
                .then(async () => {
                    // Only refresh quote metadata if both files are now completed and pricing options are changed
                    if (zipCode?.trim()?.length >= 5) {
                        const bothCompleted = incoming2dStatus && incoming3dStatus;
                        console.log('bothCompleted', bothCompleted);

                        if (bothCompleted && hasPricingOptionsChanged) {
                            await refreshQuoteMetadata();
                        }
                    }
                })
                .catch(error => {
                    notifyError(error);
                });
        }
    };
    return {
        updateLineItem,
        duplicateItem,
        removeMasterProduct,
        removeVariation,
        updateItemPrice,
        onLineItemUpdated,
    };
};
